import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";

import { AuthProvider, useAuth } from "./contexts/AuthContext";
import LoginPage from "./pages/LoginPage";
import CallbackPage from "./pages/CallbackPage";
import DashboardPage from "./pages/DashboardPage";
import VariablesPage from "./pages/VariablesPage";
import PipelinesPage from "./pages/PipelinesPage";
import PipelineRunsPage from "./pages/PipelineRunsPage";
import PipelineDetailsPage from "./pages/PipelineDetailsPage";
import SettingsPage from "./pages/SettingsPage";
import { MainLayout } from "./components/layouts/MainLayout";
import { Toaster } from "./components/ui/toaster";
import "./index.css";

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000, // 1 minute
    },
  },
});

// Protected route component with layout
const ProtectedRoute = ({ children, breadcrumbs = [] }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="fixed top-0 w-full z-50">
        <div className="w-full h-1 bg-primary/20 overflow-hidden">
          <div className="w-1/3 h-full bg-primary animate-[loading_1s_ease-in-out_infinite]"></div>
        </div>
        <div className="flex justify-center items-center min-h-screen">
          <div>Authenticating...</div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return <MainLayout breadcrumbs={breadcrumbs}>{children}</MainLayout>;
};

// App with Router
const AppWithRouter = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/callback" element={<CallbackPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute
                  breadcrumbs={[{ text: "Dashboard", href: "/dashboard" }]}
                >
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/variables/:workspace/:repoSlug"
              element={
                <ProtectedRoute
                  breadcrumbs={[
                    { text: "Dashboard", href: "/dashboard" },
                    { text: "Variables", href: "#" },
                  ]}
                >
                  <VariablesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipelines"
              element={
                <ProtectedRoute
                  breadcrumbs={[
                    { text: "Dashboard", href: "/dashboard" },
                    { text: "Bulk Run Pipelines", href: "/pipelines" },
                  ]}
                >
                  <PipelinesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipelines-status"
              element={
                <ProtectedRoute
                  breadcrumbs={[
                    { text: "Dashboard", href: "/dashboard" },
                    { text: "Pipeline Status", href: "/pipelines-status" },
                  ]}
                >
                  <PipelineRunsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipelines-status/:workspace"
              element={
                <ProtectedRoute
                  breadcrumbs={[
                    { text: "Dashboard", href: "/dashboard" },
                    { text: "Pipeline Status", href: "/pipelines-status" },
                  ]}
                >
                  <PipelineRunsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipelines-status/:workspace/:repoSlug"
              element={
                <ProtectedRoute
                  breadcrumbs={[
                    { text: "Dashboard", href: "/dashboard" },
                    { text: "Pipeline Status", href: "/pipelines-status" },
                  ]}
                >
                  <PipelineRunsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipelines-status/:workspace/:repoSlug/:pipelineUuid"
              element={
                <ProtectedRoute
                  breadcrumbs={[
                    { text: "Dashboard", href: "/dashboard" },
                    { text: "Pipeline Status", href: "/pipelines-status" },
                    { text: "Pipeline Details", href: "#" },
                  ]}
                >
                  <PipelineDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute
                  breadcrumbs={[{ text: "Settings", href: "/settings" }]}
                >
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </AuthProvider>
        <Toaster />
      </Router>
      {process.env.NODE_ENV !== "production" && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  );
};

// Main app component
const App = () => {
  return <AppWithRouter />;
};

export default App;
